import mongoose from 'mongoose';
import {
  always, complement, compose, converge, path, pick, prop, propEq, when,
} from 'ramda';
import config from '../config';
import { query as queryModel } from './util';
import { generateUserAvatar } from '../util';

const { Schema } = mongoose;

const LastOauth2LoginInfo = new Schema(
  {
    login_time: Number,
    user_agent: String,
  },
  { _id: false },
);

const IdmuserinfoSchema = new Schema({
  user_id: { type: String },
  has_publicid: { type: String },
  idmapping_count: { type: String },
  type: { type: String },
  type_id: { type: String },
  app_domain: { type: String },
  user_info: { type: String },
  misc_info: { type: String },
  public_ids: [String],
  archived_public_ids: [String],
  routing_id: { type: String },
  timestamp: { type: Number },
  auminfo: {
    user_login_type_id: { type: String },
    user_name: { type: String },
    first_name: { type: String },
    last_name: { type: String },
    middle_name: { type: String },
    nickname: { type: String },
    preferred_username: { type: String },
    profile_url: { type: String },
    website: { type: String },
    gender: { type: String },
    birthday: { type: String },
    zoneinfo: { type: String },
    locale: { type: String },
    phone_number: { type: String },
    phone_number_verified: { type: Boolean },
    address: {},
    updated_at: { type: Number },
    email: { type: String },
    email_validated: { type: Boolean },
    password: { type: String },
    avatar: { type: String },
    roles: [String],
    last_login_info: [
      {
        login_time: { type: String },
      },
    ],
    last_oauth2_login_info: [LastOauth2LoginInfo],
    number_times_login: { type: Number },
    api_version_compatibility: { type: String },
    version: { type: String },
    disabled: { type: Boolean },
    disabledDesc: { type: String },
    disabledTime: { type: Number },
  },
});

export const IdmuserinfoModel = () => mongoose.model('idmuserinfo', IdmuserinfoSchema, 'Idmuserinfo');

export const query = queryModel(IdmuserinfoModel);

const isUserCreationEvent = propEq('operationType', 'insert');

const extractUser = prop('fullDocument');

const ensureAvatar = when(
  complement(path(['auminfo', 'avatar'])),
  converge(query('findOneAndUpdate'), [
    pick(['user_id']),
    () => ({ $set: { 'auminfo.avatar': generateUserAvatar() } }),
    always({ upsert: true, new: true }),
  ]),
);

mongoose.connection.on('connected', () => IdmuserinfoModel()
  .watch()
  .on(
    'change',
    when(
      isUserCreationEvent,
      compose(
        ensureAvatar,
        extractUser,
      ),
    ),
  )
  .on('error', () => {
    // Unable to establish subscription to users collection
  }));

// NOTE: indexes are ensured by AUM service
// IdmuserinfoSchema.index({ app_domaine: 1 }, { unique: true, background: true, dropDups: true })

IdmuserinfoSchema.statics.findPortalUsers = (filter, skip, limit) => {
  const PortalUsers = IdmuserinfoModel();

  return PortalUsers.find(
    { 'auminfo.email': { $regex: `.*${filter}.*` }, app_domain: config.domain, type: { $ne: 'email_backup' } },
    (err, data) => {
      if (err) {
        return err;
      }
      return data;
    },
  )
    .limit(limit)
    .skip(skip);
};

IdmuserinfoSchema.statics.findPortalUsersForDomain = (domain, filter, skip, limit) => {
  const PortalUsers = IdmuserinfoModel();

  return PortalUsers.find(
    { 'auminfo.email': { $regex: `.*${filter}.*` }, app_domain: domain, type: { $ne: 'email_backup' } },
    (err, data) => {
      if (err) {
        return err;
      }
      return data;
    },
  )
    .limit(limit)
    .skip(skip);
};

IdmuserinfoSchema.statics.findPortalUsersCount = (filter) => {
  const PortalUsers = IdmuserinfoModel();

  return PortalUsers.find(
    { 'auminfo.email': { $regex: `.*${filter}.*` }, app_domain: config.domain, type: { $ne: 'email_backup' } },
    (err, data) => {
      if (err) {
        return err;
      }
      return data;
    },
  ).countDocuments();
};

IdmuserinfoSchema.statics.portalUsersCountInDomain = (domain, cutOffTime) => {
  const PortalUsers = IdmuserinfoModel();
  if (cutOffTime) {
    return PortalUsers.find(
      { timestamp: { $gte: cutOffTime }, app_domain: domain, type: { $ne: 'email_backup' } },
      (err, data) => {
        if (err) {
          return err;
        }
        return data;
      },
    ).count();
  }
  return PortalUsers.find({ app_domain: domain }, (err, data) => {
    if (err) {
      return err;
    }
    return data;
  }).count();
};

IdmuserinfoSchema.statics.findUserByUserID = (id) => {
  const PortalUsers = IdmuserinfoModel();

  return PortalUsers.findOne({ user_id: id });
};

IdmuserinfoSchema.statics.findUserByName = (nameFilter) => {
  const PortalUsers = IdmuserinfoModel();

  return PortalUsers.findOne({
    'auminfo.user_name': { $regex: `.*${nameFilter}.*` },
    type: { $ne: 'email_backup' },
  });
};

IdmuserinfoSchema.statics.findUsersByTeam = (teamId) => {
  const PortalUsers = IdmuserinfoModel();

  return PortalUsers.find({
    'auminfo.roles': {
      $elemMatch: {
        $regex: `^${teamId}:.*`,
      },
    },
    type: { $ne: 'email_backup' },
  });
};

IdmuserinfoSchema.statics.addUserToTeam = (userId, teamId, role) => {
  const PortalUsers = IdmuserinfoModel();

  return PortalUsers.findOneAndUpdate(
    { user_id: userId },
    {
      $push: {
        'auminfo.roles': `${teamId}:${role}`,
      },
    },
    { new: true },
  );
};

IdmuserinfoSchema.statics.removeUserFromTeam = (userId, teamId) => {
  const PortalUsers = IdmuserinfoModel();

  return PortalUsers.findOneAndUpdate(
    { user_id: userId },
    {
      $pull: {
        'auminfo.roles': {
          $regex: `^${teamId}:`,
        },
      },
    },
    { new: true },
  );
};

IdmuserinfoSchema.statics.findUserByEmail = (emailFilter) => {
  const PortalUsers = IdmuserinfoModel();

  return PortalUsers.findOne({
    'auminfo.email': { $regex: `.*${emailFilter}.*` },
    type: { $ne: 'email_backup' },
  });
};

IdmuserinfoSchema.statics.findEmailUser = (emailFilter) => {
  const PortalUsers = IdmuserinfoModel();

  return PortalUsers.findOne({
    'auminfo.email': { $regex: `.*${emailFilter}.*` },
    type: 'email',
    app_domain: config.domain,
  });
};

IdmuserinfoSchema.statics.findRootUser = () => {
  const PortalUsers = IdmuserinfoModel();

  return PortalUsers.findOne({
    'auminfo.roles': 'admininstrators:root',
    app_domain: config.domain,
  });
};

IdmuserinfoSchema.statics.migrateUser = (userId, oldUser) => {
  const PortalUsers = IdmuserinfoModel();

  return PortalUsers.update({
    user_id: userId,
  },
  { $set: { user_id: oldUser.user_id, 'auminfo.roles': oldUser.auminfo.roles } });
};

IdmuserinfoSchema.statics.updateOldId = (userId) => {
  const PortalUsers = IdmuserinfoModel();

  return PortalUsers.update({
    user_id: userId,
    type: 'email',
  },
  { $set: { user_id: `${userId}_backup`, type: 'email_backup' } });
};

export default IdmuserinfoModel;
